/**
 * usePresenca — abrir e fechar com transição (design system de movimento, "peças que abrem e
 * fecham"): mantém a peça montada enquanto ela sai e diz quando pôr `.is-aberto`.
 *
 *  - Abrir: monta fechada e, no quadro seguinte, liga `aberto` (a transição de entrada roda).
 *  - Fechar: desliga `aberto` na hora (a saída roda e pode voltar do meio se abrir de novo) e
 *    desmonta depois do token de saída (--duracao-140 por padrão, o degrau de baixo da entrada).
 *  - Sem CSS carregado (testes) o token vale 0: abre e desmonta na hora.
 *
 * Uso: `const { montado, aberto } = usePresenca(open);` e
 * `{montado && <div className={`ds-dialogo${aberto ? ' is-aberto' : ''}`}>…}`.
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { tokenMs } from '../lib/movimento';

export function usePresenca(
  aberto: boolean,
  saida: string = '--duracao-140',
): { montado: boolean; aberto: boolean } {
  const [montado, setMontado] = useState(aberto);
  const [visivel, setVisivel] = useState(false);

  useLayoutEffect(() => {
    if (aberto) setMontado(true);
  }, [aberto]);

  useEffect(() => {
    if (aberto) {
      if (!montado) return undefined;
      // Um quadro fechado antes de abrir, para a transição partir do estado fechado.
      if (typeof window.requestAnimationFrame !== 'function') {
        setVisivel(true);
        return undefined;
      }
      let segundo = 0;
      const primeiro = window.requestAnimationFrame(() => {
        segundo = window.requestAnimationFrame(() => setVisivel(true));
      });
      // Reserva: com a aba em segundo plano o navegador para os quadros, e a peça só apareceria
      // quando a aba voltasse. Com a aba à vista, os dois quadros chegam antes.
      const reserva = window.setTimeout(() => setVisivel(true), 60);
      return () => {
        window.cancelAnimationFrame(primeiro);
        if (segundo) window.cancelAnimationFrame(segundo);
        window.clearTimeout(reserva);
      };
    }
    setVisivel(false);
    if (!montado) return undefined;
    const ms = tokenMs(saida);
    if (ms <= 0) {
      setMontado(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setMontado(false), ms);
    return () => window.clearTimeout(timer);
  }, [aberto, montado, saida]);

  return { montado: montado || aberto, aberto: aberto && visivel };
}
